import { getAppStore } from '@src/utils/store-reference';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { NoPermitRequiredQueries, NPR_QUERY_KEYS } from '../query';
import { useFormStore, useModal, useRequest, useJpa, useApp } from '@stores';
import statusModal from '@pages/Forms/components/modals/statusModal';
import { RequestType, RequestTypeStringIds, StorageScopes } from '@src/utils/enums';
import { PROJECT_QUERY_KEYS } from '@src/api/services/project';

export type FetchNprParams = {
  projectID: number;
  requestID: number;
  version: number;
  requestStatus?: string;
  isGenerate?: boolean;
  archived?: boolean;
};

const nprRequestTypeIdByType = {
  [RequestType.ActivityBasedNoPermitRequired]: RequestTypeStringIds.ActivityBasedNoPermitRequired,
  [RequestType.NoPermitRequired]: RequestTypeStringIds.NoPermitRequired,
};

export const useNprRequestHandler = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const store = getAppStore();
  const { resetToDefaults: resetJpaToDefaults } = useJpa.getState();
  const { setIsLoading } = useApp.getState();
  const { setApiAnswers } = useFormStore.getState();
  const { setSelectedRequestId } = useRequest.getState();
  const { openSecondaryModal } = useModal.getState();

  const fetchNpr = async ({ isGenerate, projectID, requestID, requestStatus, version, archived }: FetchNprParams) => {
    if (!isGenerate) {
      setIsLoading(true, 'Retrieving No Permit Required Request Information...');
    }
    store.doResetRequestData();
    resetJpaToDefaults();

    try {
      const queryKey = [NPR_QUERY_KEYS.GET_NPR, projectID, requestID, version];

      queryClient.invalidateQueries({ queryKey });

      const data = await queryClient.fetchQuery({
        queryKey,
        queryFn: () => NoPermitRequiredQueries.getNpr(projectID, requestID, version),
        networkMode: 'always',
      });

      const responseData = { ...data, archived };
      const nprID = responseData?.request?.noPermitRequireds?.[0]?.noPermitRequiredID;

      useJpa.setState({ jpaBasicInfo: responseData?.request?.jpaBasicInfo });
      store.doUpdateRequestAPIData(responseData);
      store.doUpdateRequestData(responseData);
      store.doUpdateRequestObject({ requestStatus });
      store.doUpdateNprRequest(
        responseData.request.noPermitRequireds.filter((item) => item.noPermitRequiredID === nprID)[0]
      );
      setApiAnswers(responseData);
      if (isGenerate) {
        store.doGenerateDraftDocuments();
      } else {
        const nprID = responseData?.request?.noPermitRequireds?.[0]?.noPermitRequiredID;
        const requestTypeId =
          nprRequestTypeIdByType[responseData?.request?.requestType as keyof typeof nprRequestTypeIdByType];
        queryClient.invalidateQueries({
          queryKey: [
            PROJECT_QUERY_KEYS.GET_SHARED_CONTACTS,
            store.selectProjectID(),
            store.selectRequestID(),
            store.selectVersion(),
          ],
        });
        setSelectedRequestId(requestTypeId);
        navigate({
          to: '/forms/$id',
          params: { id: String(requestID) },
        });
        await store.doFetchUserFilesDetails({
          componentID: nprID,
          scope: StorageScopes.ProjectFolder,
        });
      }
      if (!isGenerate) {
        setIsLoading(false);
      }
    } catch (err) {
      console.error(err);
      openSecondaryModal(statusModal, {
        msg: 'An error occurred while retrieving the request, please try again',
        status: 'Request Retrieval Error',
      });
      setIsLoading(false);
    }
  };
  return { fetchNpr };
};
